import React, { useCallback, useEffect, useRef } from "react";
import "./NumberInput.css";
import debounce from "lodash.debounce";
import throttle from "lodash.throttle";

const NumberInput = ({
  label,
  postLabel,
  onChange,
  value,
  min,
  max,
  onFocusOut,
  disabled,
  step = 1,
  customBtnMargin
}) => {
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Debounced focus out
  const debouncedFocusOutRef = useRef(
    debounce(() => {
      if (onFocusOut) onFocusOut();
    }, 300)
  );

  useEffect(() => {
    return () => {
      debouncedFocusOutRef.current.cancel();
      throttledScroll.current.cancel();
    };
  }, []);

  // Helper to update value with min/max clamping
  const updateValue = (newValue) => {
    const clampedValue = Math.max(
      isNaN(min) ? -Infinity : min,
      Math.min(isNaN(max) ? Infinity : max, newValue)
    );
    if (clampedValue !== Number(value)) {
      onChange(clampedValue);
      debouncedFocusOutRef.current();
    }
  };

  const handleInc = () => {
    if (disabled || !onChange) return;
    updateValue(Number(value) + step);
  };

  const handleDec = () => {
    if (disabled || !onChange) return;
    updateValue(Number(value) - step);
  };

  // Throttled scroll to prevent multiple triggers per tick
  const throttledScroll = useRef(
    throttle((direction) => {
      const currentValue = Number(valueRef.current);
      const newValue = currentValue + direction * step;
      updateValue(newValue);
    }, 200) // adjust delay as needed
  );

const handleMouseWheel = (event) => {
  if (disabled || !onChange) return;
  event.preventDefault();

  const newValue = Number(value) ;
  onChange(newValue);
  debouncedFocusOutRef.current();
};


  const handleKeyDown = (event) => {
    if (disabled || !onChange) return;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      updateValue(Number(value) + step);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      updateValue(Number(value) - step);
    }
  };

  const handleChange = (e) => {
    const inputVal = e.target.value;
    if (!isNaN(inputVal)) {
      onChange(Number(inputVal));
    }
  };

  return (
    <div className="numContainer">
      {label && <span className="number-label">{label}</span>}

      <div
        className={"numContainer-component " + (disabled ? "disabled" : "")}
        style={{
          marginLeft:
            label === "Gutter"
              ? "-20px"
              : label === "Ragged Gutter"
              ? "15px"
              : "25%",
          ...customBtnMargin,
        }}
      >
        <div className="btnContainer">
          <div onClick={handleInc} className="top-btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon-tabler icon-tabler-chevron-up"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <polyline points="6 15 12 9 18 15"></polyline>
            </svg>
          </div>
          <div onClick={handleDec} className="down-btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon-tabler icon-tabler-chevron-down"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        </div>

        <span className="inputContainer">
          <input
            type="number"
            className="form-control"
            value={value}
            step={step}
            min={min}
            max={max}
            onChange={handleChange}
            onWheel={handleMouseWheel}
            onKeyDown={handleKeyDown}
            onBlur={onFocusOut}
            disabled={disabled}
          />
          {postLabel && <span className="postLValue">{postLabel}</span>}
        </span>
      </div>
    </div>
  );
};

export default NumberInput;
